import { Decimal } from 'decimal.js';
/**
 * Decimal-safe invoice arithmetic. This module is the single implementation of
 * the Portal's calculation rules. The API runs it as the authority; the web app
 * runs the very same code to preview totals while editing.
 *
 * Rules (documented in docs/data-model.md):
 *  - gross            = quantity × unit price ÷ base quantity        (unrounded)
 *  - discount         = discountAmount ?? gross × discountRate ÷ 100  → rounded to 2 dp
 *  - fee              = feeAmount ?? gross × feeRate ÷ 100            → rounded to 2 dp
 *  - line extension   = round2(gross) − discount + fee               (must be ≥ 0)
 *  - line tax         = round2(line extension × percent ÷ 100)        per tax component
 *  - tax subtotal     = Σ line tax for the same (category, percent)   — exact, no re-rounding
 *  - tax exclusive    = Σ line extensions
 *  - tax inclusive    = tax exclusive + Σ tax subtotals
 *  - payable          = tax inclusive
 * Rounding mode is ROUND_HALF_UP at two decimal places for every monetary value.
 */
export const Dec = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
export const MONEY_DP = 2;
export function dec(value) {
    if (value === null || value === undefined || value === '')
        return new Dec(0);
    return new Dec(value);
}
export function isDecimal(value) {
    if (typeof value === 'number')
        return Number.isFinite(value);
    if (typeof value !== 'string' || value.trim() === '')
        return false;
    try {
        const d = new Dec(value);
        return d.isFinite();
    }
    catch {
        return false;
    }
}
export function round2(value) {
    return value.toDecimalPlaces(MONEY_DP, Dec.ROUND_HALF_UP);
}
export function money(value) {
    return round2(dec(value)).toFixed(MONEY_DP);
}
export function sumMoney(values) {
    return values.reduce((acc, v) => acc.plus(dec(v)), new Dec(0));
}
/** Normalises a percentage to a stable string used as a grouping key / column value. */
export function percentKey(value) {
    return dec(value)
        .toDecimalPlaces(4, Dec.ROUND_HALF_UP)
        .toFixed(4);
}
export function calculateLine(input, lineNo, issues = []) {
    const at = (field) => `lines.${lineNo - 1}.${field}`;
    const zero = new Dec(0);
    const qty = isDecimal(input.invoicedQuantity) ? dec(input.invoicedQuantity) : zero;
    const price = isDecimal(input.priceAmount) ? dec(input.priceAmount) : zero;
    let base = input.baseQuantity != null && isDecimal(input.baseQuantity)
        ? dec(input.baseQuantity)
        : new Dec(1);
    if (base.lte(0)) {
        issues.push({ path: at('baseQuantity'), code: 'invalid_base_quantity', message: 'Base quantity must be greater than zero.' });
        base = new Dec(1);
    }
    if (qty.lte(0)) {
        issues.push({ path: at('invoicedQuantity'), code: 'invalid_quantity', message: 'Quantity must be greater than zero.' });
    }
    if (price.lt(0)) {
        issues.push({ path: at('priceAmount'), code: 'invalid_price', message: 'Unit price cannot be negative.' });
    }
    const gross = qty.times(price).div(base);
    const grossRounded = round2(gross);
    let discount = zero;
    if (input.discountAmount != null && input.discountAmount !== '') {
        discount = round2(dec(input.discountAmount));
    }
    else if (input.discountRate != null && input.discountRate !== '') {
        discount = round2(gross.times(dec(input.discountRate)).div(100));
    }
    let fee = zero;
    if (input.feeAmount != null && input.feeAmount !== '') {
        fee = round2(dec(input.feeAmount));
    }
    else if (input.feeRate != null && input.feeRate !== '') {
        fee = round2(gross.times(dec(input.feeRate)).div(100));
    }
    const extension = grossRounded.minus(discount).plus(fee);
    if (extension.lt(0)) {
        issues.push({
            path: at('discountAmount'),
            code: 'negative_line_total',
            message: 'The discount is larger than the line value.',
        });
    }
    const taxes = (input.taxes ?? []).map((t) => {
        const pct = dec(t.taxPercent);
        return {
            taxCategoryId: t.taxCategoryId,
            taxPercent: percentKey(t.taxPercent),
            taxableAmount: extension.toFixed(MONEY_DP),
            taxAmount: round2(extension.times(pct).div(100)).toFixed(MONEY_DP),
        };
    });
    return {
        lineNo,
        grossAmount: grossRounded.toFixed(MONEY_DP),
        discountAmount: discount.toFixed(MONEY_DP),
        feeAmount: fee.toFixed(MONEY_DP),
        lineExtensionAmount: extension.toFixed(MONEY_DP),
        taxes,
    };
}
export function calculateInvoice(lines) {
    const issues = [];
    const calculated = lines.map((l, i) => calculateLine(l, i + 1, issues));
    const groups = new Map();
    for (const line of calculated) {
        for (const t of line.taxes) {
            const key = `${t.taxCategoryId}|${t.taxPercent}`;
            const g = groups.get(key) ?? {
                taxCategoryId: t.taxCategoryId,
                taxPercent: t.taxPercent,
                taxable: new Dec(0),
                tax: new Dec(0),
            };
            g.taxable = g.taxable.plus(t.taxableAmount);
            g.tax = g.tax.plus(t.taxAmount);
            groups.set(key, g);
        }
    }
    const taxSubtotals = [...groups.values()].map((g) => ({
        taxCategoryId: g.taxCategoryId,
        taxPercent: g.taxPercent,
        taxableAmount: g.taxable.toFixed(MONEY_DP),
        taxAmount: g.tax.toFixed(MONEY_DP),
    }));
    const lineExtension = sumMoney(calculated.map((l) => l.lineExtensionAmount));
    const taxTotal = sumMoney(taxSubtotals.map((s) => s.taxAmount));
    const inclusive = lineExtension.plus(taxTotal);
    return {
        lines: calculated,
        taxSubtotals,
        lineExtensionAmount: lineExtension.toFixed(MONEY_DP),
        taxExclusiveAmount: lineExtension.toFixed(MONEY_DP),
        taxTotalAmount: taxTotal.toFixed(MONEY_DP),
        taxInclusiveAmount: inclusive.toFixed(MONEY_DP),
        payableAmount: inclusive.toFixed(MONEY_DP),
        issues,
    };
}
/** Outstanding = payable − paid − credited, never below zero. */
export function outstandingAmount(b) {
    const out = dec(b.payableAmount)
        .minus(dec(b.paidAmount))
        .minus(dec(b.creditedAmount ?? '0'));
    return (out.lt(0) ? new Dec(0) : out).toFixed(MONEY_DP);
}
export function derivePaymentStatus(b) {
    const settled = dec(b.paidAmount).plus(dec(b.creditedAmount ?? '0'));
    const payable = dec(b.payableAmount);
    if (settled.lte(0))
        return 'unpaid';
    if (settled.gte(payable))
        return 'paid';
    return 'partially_paid';
}
//# sourceMappingURL=calc.js.map